import type { Intl } from '@/intl'
import { Banner } from '@sunwebgroup/uds-ui/banner'
import * as Collapsible from '@sunwebgroup/uds-ui/collapsible'
import { Icon, type IconGlyph } from '@sunwebgroup/uds-ui/icon'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import * as React from 'react'
import { useMemo } from 'react'
import { useIntl } from '@/intl'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { HighlightedPaymentMethod } from '@/scopes/prices/components/HighlightedPaymentMethod'
import { BookNowButton } from '@/scopes/prices/components/PriceBasket/BookNowButton'
import { ShareButton } from '@/scopes/prices/components/PriceBasket/ShareButton'
import { isAccommodationAvailableSelector, paymentMethodSelector, transportResponseSelector } from '@/scopes/prices/components/selectors'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import styles from './PriceBreakdown.module.css'

const PACKAGE_USP: Record<string, IconGlyph> = {
  Discount: 'OptionalFacilities',
  FreeCancellation: 'ThumbsUp',
  AccoCampaigns: 'CheckMark',
  CarRentalIncluded: 'CarFront',
  DownPayment: 'Deposit',
  HandLuggageIncluded: 'LuggageCheck',
  TransferIncluded: 'BusRight',
  SkiPassIncluded: 'SkiPass',
  SkiMaterialIncluded: 'Skis',
}

const DEFAULT_USP_ICON: IconGlyph = 'CheckMark'

const getUspGlyph = (type: string): IconGlyph => PACKAGE_USP[type] ?? DEFAULT_USP_ICON

export function PriceBreakdown() {
  const intl = useIntl()
  const catalogueType = useConfiguration('catalogueType')
  const { priceBreakdown, transportType } = usePricesSelector(transportResponseSelector)
  const { paymentMethod } = usePricesSelector(paymentMethodSelector)
  const [reservationCostsOpen, setReservationCostsOpen] = React.useState(true)
  const [destinationCostsOpen, setDestinationCostsOpen] = React.useState(true)
  const extraCostsMessage = intl.formatMessage({
    id: catalogueType === 'winter'
      ? 'price_basket_price_breakdown_extra_costs_message_winter'
      : 'price_basket_price_breakdown_extra_costs_message_summer',
  })

  if (!priceBreakdown)
    return null

  const hasDiscount = priceBreakdown.totalGrossPrice > priceBreakdown.totalPrice

  return (
    <>
      <Collapsible.Root className={styles.section} defaultOpen onOpenChange={setReservationCostsOpen} open={reservationCostsOpen}>
        <Collapsible.Trigger asChild>
          <div className={styles.collapsibleHeader}>
            <Typography variant="body-m-bold-tight">{intl.formatMessage({ id: 'price_basket_price_breakdown_pay_reservation' })}</Typography>
            <div className={styles.trailing}>
              {priceBreakdown.atReservation.subtotal > 0 && (
                <Typography variant="body-m-bold-tight">{intl.formatCurrency(priceBreakdown.atReservation.subtotal)}</Typography>
              )}
              <Icon className={styles.chevron} glyph={reservationCostsOpen ? 'ChevronUpExtraSmall' : 'ChevronDownExtraSmall'} />
            </div>
          </div>
        </Collapsible.Trigger>
        <Collapsible.Content>
          <div className={styles.content}>
            {priceBreakdown.atReservation.rooms.map((item, index) => (
              <RoomItem key={index} transportType={transportType} id={index + 1} items={item} />
            ))}
            {priceBreakdown.atReservation.services.map((item, index) => (
              <PriceBreakdownItem key={index} {...item} variant={item.price >= 0 ? 'default' : 'discount'} />
            ))}
            {priceBreakdown.atReservation.subtotal > 0 && (
              <PriceBreakdownItem
                header={intl.formatMessage({ id: 'price_basket_price_breakdown_subtotal' })}
                price={priceBreakdown.atReservation.subtotal}
                variant="bold"
              />
            )}
          </div>
        </Collapsible.Content>
      </Collapsible.Root>
      {priceBreakdown.atDestination.subtotal > 0 && (
        <Collapsible.Root className={cn(styles.section, styles.sectionTinted)} defaultOpen onOpenChange={setDestinationCostsOpen} open={destinationCostsOpen}>
          <Collapsible.Trigger asChild>
            <div className={styles.collapsibleHeader}>
              <Typography variant="body-m-bold-tight">{intl.formatMessage({ id: 'price_basket_price_breakdown_pay_destination' })}</Typography>
              <div className={styles.trailing}>
                <Typography variant="body-m-bold-tight">{intl.formatCurrency(priceBreakdown.atDestination.subtotal)}</Typography>
                <Icon className={styles.chevron} glyph={destinationCostsOpen ? 'ChevronUpExtraSmall' : 'ChevronDownExtraSmall'} />
              </div>
            </div>
          </Collapsible.Trigger>
          <Collapsible.Content>
            <div className={styles.content}>
              {priceBreakdown.atDestination.items.map((item, index) => (
                <PriceBreakdownItem key={index} {...item} variant={item.price ? 'default' : 'discount'} />
              ))}
              <PriceBreakdownItem
                header={intl.formatMessage({ id: 'price_basket_price_breakdown_subtotal' })}
                price={priceBreakdown.atDestination.subtotal}
                variant="bold"
              />
            </div>
            {priceBreakdown.extraCostsText && (
              <div className={styles.extraCostsWrapper}>
                <Banner text={priceBreakdown.extraCostsText.title} supportingText={priceBreakdown.extraCostsText.text} size="M" color="neutral" />
              </div>
            )}
          </Collapsible.Content>
        </Collapsible.Root>
      )}
      <div className={styles.subtotalsWrapper}>
        {hasDiscount && (
          <div className={styles.grossPrice}>
            <Typography variant="body-s-regular-tight">{intl.formatCurrency(priceBreakdown.totalGrossPrice)}</Typography>
          </div>
        )}
        <div className={styles.textLine}>
          <Typography variant="body-l-bold-tight">
            {intl.formatMessage({ id: 'price_basket_price_breakdown_total' })}
          </Typography>
          <Typography variant="body-l-bold-tight">{intl.formatCurrency(priceBreakdown.totalPrice)}</Typography>
        </div>
        <div className={styles.textLine}>
          <Typography variant="body-s-regular-tight">{intl.formatMessage({ id: 'price_basket_price_breakdown_price_per_person' })}</Typography>
          <Typography variant="body-s-regular-tight">{intl.formatCurrency(priceBreakdown.pricePerPerson)}</Typography>
        </div>
        <div className={styles.divider} />
        {priceBreakdown.atReservation.subtotal > 0 && (
          <div className={styles.textLine}>
            <Typography variant="body-s-regular-tight">{intl.formatMessage({ id: 'price_basket_price_breakdown_pay_reservation' })}</Typography>
            <Typography variant="body-s-regular-tight">{intl.formatCurrency(priceBreakdown.atReservation.subtotal)}</Typography>
          </div>
        )}
        {priceBreakdown.atDestination.subtotal > 0 && (
          <div className={styles.textLine}>
            <Typography variant="body-s-regular-tight">{intl.formatMessage({ id: 'price_basket_price_breakdown_pay_destination' })}</Typography>
            <Typography variant="body-s-regular-tight">{intl.formatCurrency(priceBreakdown.atDestination.subtotal)}</Typography>
          </div>
        )}
      </div>
      <div className={styles.footerWrapper}>
        <BookNowButton />
        <ShareButton />
        {paymentMethod?.text && paymentMethod?.icon && (<HighlightedPaymentMethod text={paymentMethod?.text} icon={paymentMethod?.icon} />)}
        {extraCostsMessage && <Banner color="neutral" icon="CarDoor" text={extraCostsMessage} size="M" />}
        <div className={styles.uspsWrapper}>
          {priceBreakdown.usps.map((usp, index) => (
            <Usp key={index} {...usp} />
          ))}
        </div>
      </div>
    </>
  )
}

interface RoomItemProps {
  id: number
  transportType?: string
  items: {
    price: number
    valueText: string
    description: string
    isDiscount: boolean
  }[]
}
const TRANSPORT_TYPE_TRANSLATION_KEYS: Record<string, string> = {
  Flight: 'prices_room_selector_room_group-transport-flight',
  Bus: 'prices_room_selector_room_group-transport-bus',
}

interface GetTransportTypeTextParams {
  transportType?: string
  header: string
  isFlightOnly: boolean
  intl: Intl
}

function getTransportTypeText({ transportType, header, isFlightOnly, intl }: GetTransportTypeTextParams) {
  if (transportType === 'SelfDrive') {
    return header
  }

  const transportTranslationKey = transportType ? TRANSPORT_TYPE_TRANSLATION_KEYS[transportType] : undefined
  if (!transportTranslationKey) {
    return ''
  }

  const roomPrefix = isFlightOnly ? '' : `${header} + `
  return `${roomPrefix}${intl.formatMessage({ id: transportTranslationKey })}`
}

function RoomItem({ id, items, transportType }: RoomItemProps) {
  const intl = useIntl()
  const { isFlightOnly } = usePricesSelector(isAccommodationAvailableSelector)

  const header = intl.formatMessage({ id: `prices_room_selector_room_group-title` }, { roomNumber: id })

  const transportTypeText = useMemo(
    () => getTransportTypeText({ transportType, header, isFlightOnly, intl }),
    [transportType, header, isFlightOnly, intl],
  )
  return (
    <div>
      <div className={styles.rowHeader}>
        <Typography>
          {transportTypeText}
        </Typography>
        <Typography className={styles.strongText}>{intl.formatCurrency(items.reduce((acc, item) => acc + item.price, 0))}</Typography>
      </div>
      {items.map((item, index) => (
        <div className={styles.ageProfileWrapper} key={index}>
          <Typography className={styles.strongText}>{item.valueText}</Typography>
        </div>
      ))}
    </div>
  )
}
interface PriceBreakdownItemProps {
  header: string
  price: number
  valueText?: string
  description?: string
  variant?: 'bold' | 'discount' | 'default'
}

function PriceBreakdownItem({ header, price, valueText, description, variant }: PriceBreakdownItemProps) {
  const intl = useIntl()
  const trailing = price ? `${intl.formatCurrency(price, { showSign: variant === 'discount' })}` : valueText
  const isBold = variant === 'bold'

  return (
    <div className={cn(variant === 'discount' && styles.priceRowDiscount)}>
      <div className={styles.rowHeader}>
        <Typography variant={isBold ? 'body-m-bold-tight' : undefined}>{header}</Typography>
        <Typography className={styles.strongText} variant={isBold ? 'body-m-bold-tight' : undefined}>{trailing}</Typography>
      </div>
      {description && <Typography className={styles.strongText} variant={isBold ? 'body-m-bold-tight' : undefined}>{description}</Typography>}
    </div>
  )
}

function Usp({ type, title, text }: { type: string, title: string, text?: string | undefined }) {
  return (
    <div className={styles.usp}>
      <Icon className={styles.uspIcon} glyph={getUspGlyph(type)} />
      <div className={styles.textWrapper}>
        <Typography variant="body-m-bold-tight">{title}</Typography>
        {text && <Typography variant="body-m-regular-tight">{text}</Typography>}
      </div>
    </div>
  )
}
